/**
 * The laser pointer (SRS C4).
 *
 * A red trail that follows the hand and fades away in a second and a half. It is not
 * ink: it never reaches the board document, it is never saved, and it survives no
 * reload. That is the whole point of it — a teacher pointing at a step of a worked
 * example wants the class to look there now, not to find a red scribble over the
 * working afterwards.
 *
 * The trail lives here rather than on a canvas because it has to keep fading while
 * the hand is perfectly still: every frame drops whatever has run out of time and
 * asks for the overlay to be drawn again, until there is nothing left to draw.
 */

import type { Point } from '@opal/shared';

import { animationFrames, type FrameScheduler } from './paint-scheduler.js';

/** SRS C4 — how long a mark takes to disappear once it is made. */
export const LASER_FADE_MS = 1500;

/** How wide the beam is on screen, at every zoom. It points; it does not measure. */
export const LASER_SCREEN_WIDTH = 6;

/** One point of the trail, in canvas coordinates, with the moment it was made. */
export interface LaserMark {
  readonly x: number;
  readonly y: number;
  readonly at: number;
}

export interface LaserSessionOptions {
  /** Asks for the overlay to be painted again. */
  readonly onChanged: () => void;
  readonly now?: () => number;
  readonly frames?: FrameScheduler;
}

export interface LaserSession {
  begin(at: Point): void;
  extend(points: readonly Point[]): void;
  /** The hand lifted. What is already drawn carries on fading on its own. */
  finish(): void;
  /** The gesture was taken away: the trail goes at once. */
  cancel(): void;
  /** The trail as it stands, oldest first, with anything faded out left behind. */
  trail(now: number): readonly LaserMark[];
  dispose(): void;
}

interface State {
  readonly options: LaserSessionOptions;
  readonly frames: FrameScheduler;
  readonly now: () => number;
  marks: LaserMark[];
  frame: number | null;
}

/** Everything still within its life, oldest first. */
function living(marks: readonly LaserMark[], now: number): LaserMark[] {
  return marks.filter((mark) => now - mark.at < LASER_FADE_MS);
}

/**
 * Keeps the overlay painting while anything is still fading.
 *
 * Stops the moment the trail is empty, so a board nobody is pointing at costs
 * nothing at all.
 */
function keepFading(state: State): void {
  if (state.frame !== null) return;
  state.frame = state.frames.request(() => {
    state.frame = null;
    state.marks = living(state.marks, state.now());
    state.options.onChanged();
    if (state.marks.length > 0) keepFading(state);
  });
}

function add(state: State, points: readonly Point[]): void {
  const at = state.now();
  for (const point of points) state.marks.push({ x: point.x, y: point.y, at });
  state.options.onChanged();
  keepFading(state);
}

export function createLaserSession(options: LaserSessionOptions): LaserSession {
  const state: State = {
    options,
    frames: options.frames ?? animationFrames,
    now: options.now ?? Date.now,
    marks: [],
    frame: null,
  };

  const stop = (): void => {
    if (state.frame === null) return;
    state.frames.cancel(state.frame);
    state.frame = null;
  };

  return {
    begin(at) {
      // A new sweep starts its own trail: the last one is left to fade where it is.
      add(state, [at]);
    },

    extend(points) {
      add(state, points);
    },

    finish() {
      // Nothing to do. The trail is already fading, and the hand lifting does not
      // hurry it along — a teacher who points and holds still is still pointing.
    },

    cancel() {
      state.marks = [];
      stop();
      state.options.onChanged();
    },

    trail(now) {
      return living(state.marks, now);
    },

    dispose() {
      state.marks = [];
      stop();
    },
  };
}
